import { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { learningProgressScope, readLearningProgress, resumeLearningPosition, saveLearningProgress } from "../../lib/learningProgress";
import { PLAYBACK_RATES, readPlaybackRate, rememberPlaybackRate } from "../../lib/videoPlayback";
import { LessonCompletion } from "./LessonCompletion";
import { Modal } from "./Overlay";
import { Tip } from "./Tag";
import { Button } from "./Button";
import { useT } from "../../lib/i18n";
import { LEGACY_ECON_LESSONS, courseHash, courseOf, helpVideo, lessonReady, type HelpCourseId, type HelpVideoDef, type HelpVideoId } from "../../lib/helpVideos";
import { BScaleContext } from "../data/BCoins";
import { lessonShareUrl } from "../../lib/helpVideoRoute";
import { copyText } from "../../lib/clipboard";
import { showToast } from "../../lib/toast";

/**
 * 参数说明视频入口。视频与文案都从 lib/helpVideos.ts 的清单读，调用方只给 id。
 *
 * - `link`：一个不起眼的文字按钮（「B 值是什么？看视频」），放在页签旁
 * - `icon`：字段标签旁一个 12px 的播放图标，悬停提示「看视频：E 是什么」，不占行高
 * - `watch`：和 `link` 一样的文字按钮，写成「看视频：代理管理」——标题不是问句的课（页面整体讲解）用它
 *
 * 视频与封面在 CDN 上（清单里是完整 https 链接）。preload="none"：不点开不下载。
 */
export function HelpVideo({ id, variant = "link" }: { id: HelpVideoId; variant?: "link" | "icon" | "watch" }) {
  const t = useT();
  const legacyHidden = useContext(BScaleContext).model === 2 && LEGACY_ECON_LESSONS.has(id);
  const [open, setOpen] = useState(false);
  const v = helpVideo(id);
  const title = t(v.title);
  if (legacyHidden) return null;
  return (
    <>
      {variant === "icon" ? (
        <Tip text={t("hv.watch", { title })}>
          <button type="button" onClick={() => setOpen(true)} aria-label={t("hv.watch", { title })}
            className="inline-flex text-primary hover:opacity-80">
            <PlayIcon />
          </button>
        </Tip>
      ) : (
        <button type="button" onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
          <PlayIcon />
          {t(variant === "watch" ? "hv.watch" : "hv.linkLabel", { title })}
        </button>
      )}
      {open && <HelpVideoModal video={v} onClose={() => setOpen(false)} />}
    </>
  );
}

/**
 * 播放弹窗。关闭即卸载 <video>，播放随之停止，不用自己 pause。
 * `onPick` 给了就在底部显示上一课 / 下一课（课程页用），只在本门课程内按清单顺序走。
 * 视频还没传的课显示「视频制作中」，不放空的 <video>。
 * 标题旁的「复制链接」复制课程中心里这一节的直达地址，不管弹窗是从哪一页打开的。
 */
export function HelpVideoModal({ video, onClose, onPick, operator }: {
  video: HelpVideoDef; onClose: () => void; onPick?: (v: HelpVideoDef) => void; operator?: string | null;
}) {
  const t = useT();
  const hideLegacy = useContext(BScaleContext).model === 2;
  const lessons = courseOf(video.id).lessons.filter((x) => !hideLegacy || !LEGACY_ECON_LESSONS.has(x.id));
  const i = lessons.findIndex((x) => x.id === video.id);
  const prev = i > 0 ? lessons[i - 1] : undefined;
  const next = lessons[i + 1];
  const footer = onPick && (prev || next) ? (
    <>
      {prev && <Button size="sm" onClick={() => onPick(prev)}>{t("hv.prev", { title: t(prev.title) })}</Button>}
      {next && <Button size="sm" tone="primary" onClick={() => onPick(next)}>{t("hv.next", { title: t(next.title) })}</Button>}
    </>
  ) : undefined;
  return (
    <Modal title={<span className="flex flex-wrap items-center gap-x-3 gap-y-1">{t("hv.modalTitle", { title: t(video.title) })}<CopyLessonLink id={video.id} /></span>}
      onClose={onClose} width={800} footer={footer}>
      {/* Each media instance owns its lesson and account scope throughout its lifetime. */}
      {lessonReady(video) ? (
        <LearningVideo key={learningProgressScope(operator)} video={video} scope={learningProgressScope(operator)}
          onDone={onClose} onContinue={onPick && next && lessonReady(next) ? () => onPick(next) : undefined}
          nextTitle={onPick && next && lessonReady(next) ? t(next.title) : undefined} />
      ) : (
        <div className="flex aspect-video w-full flex-col items-center justify-center gap-2 rounded-base bg-fill-lighter text-center">
          <span className="text-sm font-semibold text-ink-regular">{t("hv.pending")}</span>
          <span className="max-w-md px-4 text-xs text-ink-secondary">{t(video.summary)}</span>
        </div>
      )}
    </Modal>
  );
}

type NativeFullscreenVideo = HTMLVideoElement & {
  webkitEnterFullscreen?: () => void;
  webkitExitFullscreen?: () => void;
  webkitDisplayingFullscreen?: boolean;
};

const ownsFullscreen = (player: HTMLElement | null) => !!document.fullscreenElement && !!player?.contains(document.fullscreenElement);

function LearningVideo({ video, scope, nextTitle, onContinue, onDone }: {
  video: HelpVideoDef; scope: string; nextTitle?: string; onContinue?: () => void; onDone: () => void;
}) {
  const t = useT();
  const playerRef = useRef<HTMLDivElement>(null);
  const [rate, setRate] = useState(() => readPlaybackRate(scope));
  const [fullscreen, setFullscreen] = useState(false);
  const [fullscreenError, setFullscreenError] = useState(false);
  const [celebrating, setCelebrating] = useState(false);
  const completionShown = useRef(false);
  const lastSaved = useRef(-1);
  const mediaRef = useRef<HTMLVideoElement>(null);
  const record = (media: HTMLVideoElement, completed = false) => {
    if (!Number.isFinite(media.duration) || media.duration <= 0 || media.currentTime <= 0 && !completed) return false;
    saveLearningProgress(scope, video.id, { position: media.currentTime, duration: media.duration, completed });
    lastSaved.current = media.currentTime;
    return true;
  };
  useLayoutEffect(() => {
    completionShown.current = false;
    setCelebrating(false);
  }, [video.id]);
  useEffect(() => {
    const media = mediaRef.current;
    lastSaved.current = -1;
    const flush = () => { if (media) record(media); };
    window.addEventListener("pagehide", flush);
    return () => { window.removeEventListener("pagehide", flush); flush(); media?.pause(); };
  }, [scope, video.id]);
  useEffect(() => {
    const media = mediaRef.current;
    if (!media) return;
    media.defaultPlaybackRate = rate;
    media.playbackRate = rate;
  }, [rate, video.id]);
  const reflectFullscreen = () => setFullscreen(ownsFullscreen(playerRef.current) || !!(mediaRef.current as NativeFullscreenVideo | null)?.webkitDisplayingFullscreen);
  useEffect(() => {
    const player = playerRef.current;
    document.addEventListener("fullscreenchange", reflectFullscreen);
    return () => {
      document.removeEventListener("fullscreenchange", reflectFullscreen);
      if (ownsFullscreen(player)) void document.exitFullscreen().catch(() => {});
    };
  }, []);
  useEffect(() => {
    const media = mediaRef.current as NativeFullscreenVideo | null;
    media?.addEventListener("webkitbeginfullscreen", reflectFullscreen);
    media?.addEventListener("webkitendfullscreen", reflectFullscreen);
    return () => {
      media?.removeEventListener("webkitbeginfullscreen", reflectFullscreen);
      media?.removeEventListener("webkitendfullscreen", reflectFullscreen);
      try { if (media?.webkitDisplayingFullscreen) media.webkitExitFullscreen?.(); } catch { /* Native video can already be closing. */ }
    };
  }, [video.id]);
  const toggleFullscreen = async () => {
    const player = playerRef.current;
    const media = mediaRef.current as NativeFullscreenVideo | null;
    setFullscreenError(false);
    try {
      if (ownsFullscreen(player)) await document.exitFullscreen();
      else if (media?.webkitDisplayingFullscreen && media.webkitExitFullscreen) media.webkitExitFullscreen();
      else if (player?.requestFullscreen && document.fullscreenEnabled !== false) await player.requestFullscreen();
      else if (media?.webkitEnterFullscreen) media.webkitEnterFullscreen();
      else setFullscreenError(true);
    } catch { setFullscreenError(true); }
  };
  useEffect(() => {
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && (ownsFullscreen(playerRef.current) || (mediaRef.current as NativeFullscreenVideo | null)?.webkitDisplayingFullscreen)) {
        event.preventDefault();
        event.stopPropagation();
        void toggleFullscreen();
      }
    };
    // Changing lessons replaces the focused button; Escape may then target the body.
    document.addEventListener("keydown", onEscape, true);
    return () => document.removeEventListener("keydown", onEscape, true);
  }, []);
  return <div ref={playerRef}
    className="overflow-hidden rounded-base bg-black [&:fullscreen]:flex [&:fullscreen]:h-full [&:fullscreen]:w-full [&:fullscreen]:flex-col [&:fullscreen]:rounded-none">
    <div style={{ containerType: "size", containerName: "lesson-stage" }} className={fullscreen ? "relative min-h-0 w-full flex-1" : "relative aspect-video w-full"}>
    <video
      key={video.id}
      ref={mediaRef}
      src={absolute(video.src)}
      poster={video.poster ? absolute(video.poster) : undefined}
      controls autoPlay playsInline preload="none"
      className="absolute inset-0 h-full w-full bg-black object-contain"
      onLoadedMetadata={(event) => {
        const media = event.currentTarget;
        media.defaultPlaybackRate = rate;
        media.playbackRate = rate;
        const position = resumeLearningPosition(readLearningProgress(scope).progress[video.id], media.duration);
        if (position > 0) media.currentTime = position;
      }}
      onRateChange={(event) => {
        const next = event.currentTarget.playbackRate;
        setRate(next);
        rememberPlaybackRate(scope, next);
      }}
      onTimeUpdate={(event) => {
        const media = event.currentTarget;
        if (Math.abs(media.currentTime - lastSaved.current) >= 5) record(media);
      }}
      onPause={(event) => record(event.currentTarget)}
      onPlay={(event) => {
        if (event.currentTarget.currentTime < event.currentTarget.duration - 0.5) {
          completionShown.current = false;
          setCelebrating(false);
        }
      }}
      onSeeked={(event) => {
        if (celebrating && event.currentTarget.currentTime < event.currentTarget.duration - 0.5) {
          completionShown.current = false;
          setCelebrating(false);
        }
      }}
      onEnded={(event) => {
        const media = event.currentTarget as NativeFullscreenVideo;
        if (!record(media, true) || completionShown.current) return;
        completionShown.current = true;
        const celebrate = async () => {
          // Native video fullscreen cannot show sibling overlays. Return to the player first.
          try {
            if (document.fullscreenElement === media) await document.exitFullscreen();
            else if (media.webkitDisplayingFullscreen) media.webkitExitFullscreen?.();
          } catch { /* The browser can already have exited fullscreen at the end. */ }
          if (mediaRef.current === media && completionShown.current && media.ended) setCelebrating(true);
        };
        void celebrate();
      }}
    />
    {celebrating && <LessonCompletion title={t(video.title)} nextTitle={nextTitle} onContinue={onContinue} onDone={onDone}
      onReplay={() => {
        const media = mediaRef.current;
        if (!media) return;
        completionShown.current = false;
        setCelebrating(false);
        media.currentTime = 0;
        void media.play().catch(() => {});
      }} />}
    </div>
    <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 bg-fill-light px-3 py-2 text-sm text-ink">
      <label className="flex items-center gap-2">{t("hv.playbackSpeed")}
        <select value={rate} onChange={(event) => setRate(Number(event.target.value))}
          className="h-8 rounded-base border border-line bg-white px-2 text-sm focus:border-primary focus:outline-none">
          {!PLAYBACK_RATES.some((value) => value === rate) && <option value={rate}>{rate}×</option>}
          {PLAYBACK_RATES.map((value) => <option key={value} value={value}>{value}×</option>)}
        </select>
      </label>
      <button type="button" onClick={() => { void toggleFullscreen(); }} aria-pressed={fullscreen}
        className="h-8 rounded-base border border-line bg-white px-3 text-sm font-medium text-ink hover:border-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">
        {t(fullscreen ? "hv.exitFullscreen" : "hv.fullscreen")}
      </button>
      {fullscreenError && <p role="status" className="w-full text-xs text-danger-800">{t("hv.fullscreenFailed")}</p>}
    </div>
  </div>;
}

/**
 * 「本页课程」：一个不起眼的文字按钮，跳到课程中心并定位到这门课程的分区。
 * 直接改 hash（useHashRoute 只认 / 前面的页面 id，课程中心自己读后面那段）
 */
export function CourseLink({ course }: { course: HelpCourseId }) {
  const t = useT();
  return (
    <button type="button" onClick={() => { window.location.hash = courseHash(course); }}
      className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
      <PlayIcon />
      {t("hv.pageCourse")}
    </button>
  );
}

/**
 * 「复制链接」：复制这一节在课程中心的直达地址（`#help-videos/<课程>/<课>`），不带任何查询参数（见 lessonShareUrl）。
 * 剪贴板写不进去（iframe 没给权限等）时，把地址摆进一个已选中的只读输入框让人手动复制。
 */
export function CopyLessonLink({ id, className = "" }: { id: HelpVideoId; className?: string }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  const [manual, setManual] = useState<string | null>(null);
  useEffect(() => {
    if (!copied) return;
    const h = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(h);
  }, [copied]);
  const copy = async () => {
    const url = lessonShareUrl(window.location, id);
    if (await copyText(url)) {
      setManual(null);
      setCopied(true);
      showToast(t("hv.linkCopied"));
    } else {
      setManual(url);
      showToast(t("hv.copyManual"), true);
    }
  };
  return (
    <span className={`inline-flex items-center gap-2 text-xs font-normal ${className}`}>
      <button type="button" onClick={() => { void copy(); }} aria-label={t("hv.copyLinkLabel", { title: t(helpVideo(id).title) })}
        className="inline-flex items-center gap-1 rounded-base text-primary hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">
        <LinkIcon />
        {t(copied ? "hv.copied" : "hv.copyLink")}
      </button>
      {manual && <input readOnly value={manual} aria-label={t("hv.copyManual")} autoFocus onFocus={(e) => e.currentTarget.select()}
        className="h-6 w-64 max-w-full rounded-base border border-line bg-white px-1.5 text-xs text-ink focus:border-primary focus:outline-none" />}
    </span>
  );
}

function LinkIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true" className="flex-none" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <path d="M6.5 9.5l3-3" />
      <path d="M7 4.5l1-1a2.8 2.8 0 014 4l-1 1" />
      <path d="M9 11.5l-1 1a2.8 2.8 0 01-4-4l1-1" />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true" className="flex-none">
      <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <path d="M6.5 5.2v5.6L11 8z" fill="currentColor" />
    </svg>
  );
}

/**
 * 相对地址按 location 补成绝对地址：被 wujie 默认模式承载时 DOM 渲染在宿主文档的 shadow DOM 里，
 * 根路径的 "/assets/…" 会被解析到宿主域名；wujie 代理过的 location 指向本应用，补全后两种承载都对。
 * 清单里的 CDN 链接本来就是绝对地址，原样返回。
 */
export function absolute(url: string) {
  if (/^https?:\/\//i.test(url)) return url;
  try { return new URL(url, window.location.href).href; } catch { return url; }
}
